import { Button as MuiButton } from '@mui/material';
import { Palette, styled } from '@mui/material/styles';

declare module '@mui/material/Button' {
  interface ButtonOwnProps {
    padding?: string;
    fontSize?: string | number;
  }

  interface ButtonPropsSizeOverrides {
    sm: true;
    md: true;
    lg: true;
    xl: true;
    '2xl': true;
    small: false;
    medium: false;
    large: false;
  }

  interface ButtonPropsColorOverrides {
    primary: true;
    secondary: true;
    success: true;
    warning: true;
    error: true;
  }

  interface ButtonPropsVariantOverrides {
    contained: true;
    outlined: true;
    text: true;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColorMapping = (palette: Palette): any => ({
  primary: {
    contained: {
      color: palette.common.white,
      borderColor: palette.primary[600],
      backgroundColor: palette.primary[600],
      ':hover': {
        borderColor: palette.primary[600],
        backgroundColor: palette.primary[700],
      },
      ':focus': {
        boxShadow:
          '0px 0px 0px 4px rgba(244, 235, 255, 1), 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      },
      ':disabled': {
        color: palette.common.white,
        borderColor: palette.primary[200],
        backgroundColor: palette.primary[200],
      },
    },
    outlined: {
      color: palette.grey[900],
      borderColor: palette.grey[300],
      backgroundColor: palette.background.paper,
      ':hover': {
        borderColor: palette.grey[300],
        backgroundColor: palette.grey[50],
      },
      ':focus': {
        boxShadow:
          '0px 0px 0px 4px rgba(242, 244, 247, 1), 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      },
      ':disabled': {
        backgroundColor: palette.grey[200],
      },
    },
    text: {
      color: palette.grayBlue?.main,
      boxShadow: 'unset',
      ':focus': {
        boxShadow: 'unset',
      },
      ':disabled': {
        '& .MuiButton-icon': {
          color: 'unset',
        },
      },
      '& .MuiButton-icon': {
        color: palette.grayBlue?.main,
      },
    },
  },
  secondary: {
    contained: {
      color: palette.primary[700],
      backgroundColor: palette.primary[50],
      borderColor: palette.primary[200],
      ':hover': {
        borderColor: palette.primary[200],
        backgroundColor: palette.primary[100],
      },
      ':focus': {
        boxShadow:
          '0px 0px 0px 4px rgba(242, 244, 247, 1), 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      },
      ':disabled': {
        color: palette.primary[300],
        backgroundColor: palette.primary[25],
        borderColor: palette.primary[25],
      },
    },
    outlined: {
      color: palette.grey[600],
      borderColor: palette.grey[300],
      ':hover': {
        borderColor: palette.grey[300],
      },
      ':focus': {
        boxShadow:
          '0px 0px 0px 4px rgba(242, 244, 247, 1), 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      },
      ':disabled': {
        backgroundColor: palette.grey[100],
      },
    },
    text: {
      boxShadow: 'unset',
    },
  },
  success: {
    borderColor: palette.success[300],
    ':hover': {
      borderColor: palette.success[300],
    },
    ':focus': {
      boxShadow:
        '0px 0px 0px 4px rgba(244, 235, 255, 1), 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    ':disabled': {
      backgroundColor: palette.success[200],
    },
  },
  warning: {
    borderColor: palette.warning[300],
    ':hover': {
      borderColor: palette.warning[300],
    },
    ':focus': {
      boxShadow:
        '0px 0px 0px 4px rgba(254, 228, 226, 1), 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    ':disabled': {
      backgroundColor: palette.warning[200],
    },
  },
  error: {
    borderColor: palette.error[300],
    ':hover': {
      borderColor: palette.error[300],
    },
    ':focus': {
      boxShadow:
        '0px 0px 0px 4px rgba(254, 228, 226, 1), 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    ':disabled': {
      color: palette.error[300],
      backgroundColor: palette.error[50],
    },
  },
});

export const StyledButton = styled(MuiButton)(({
  padding,
  fontSize,
  color = 'primary',
  variant = 'contained',
  theme: { spacing, palette, shadowSizes, typography },
}) => {
  const colorMapping = getColorMapping(palette)[color];

  return {
    borderRadius: spacing(2),
    fontWeight: 600,
    fontSize,
    padding,
    boxShadow: shadowSizes.xs,
    fontFamily: typography.fontFamily,
    ...(variant === 'contained' && { border: '1px solid' }),
    ...(colorMapping[variant] ? colorMapping[variant] : colorMapping),
  };
});
